import NavBarDrawer from 'components/layout/navbar/navbar-drawer';
import useStores from 'hooks/use-stores.hooks';
import useIsMobile from 'hooks/use-is-mobile';
import { css } from '@emotion/react';
import styled from 'types/theme.types';
import { PanelType, usePanelType } from 'hooks/use-panel-type';
import { Panel, PanelSection } from 'components/layout/navbar/navbar-panel.utils';
import ProjectMenu from 'components/layout/navbar/navbar-project-menu';
import OrganizationMenu from 'components/layout/navbar/navbar-organization-menu';
import { useCurrentProjectId } from 'hooks/path-params-extraction.hooks';
import {
  legacyOnly,
  newDesignOnly,
  NAVBAR_WIDTH,
  NAVBAR_COLLAPSED_WIDTH,
  NAVBAR_TRANSITION_DURATION
} from 'constants/themes.constants';
import NavbarCollapse from 'components/layout/navbar/navbar-collapse';
import AnimatedPanel from 'components/common/animated-panel';
import { NavbarCollapseProvider, useNavbarCollapse } from 'components/layout/navbar/navbar-collapse-context';

const NavBar = () => {
  const { isDesktop } = useIsMobile();
  const {
    organizationsStore: { currentOrganization }
  } = useStores();
  const panelType = usePanelType();
  const activeProjectId = useCurrentProjectId();

  return (
    <NavbarCollapseProvider data-e2e="navbar-container">
      <NavBarDrawer bypass={isDesktop}>
        <NavBarContainerWithContext>
          {currentOrganization && (
            <>
              <MainContainer>
                <PanelsContainer>
                  <Panel>
                    <AnimatedPanel animationKey={panelType}>
                      <PanelSection data-e2e={`navbar-${panelType}-menu`}>
                        {panelType === PanelType.Organization ? (
                          <OrganizationMenu organization={currentOrganization} />
                        ) : (
                          <ProjectMenu projectId={activeProjectId} />
                        )}
                      </PanelSection>
                    </AnimatedPanel>
                  </Panel>
                </PanelsContainer>
              </MainContainer>
              {isDesktop && <NavbarCollapse />}
            </>
          )}
        </NavBarContainerWithContext>
      </NavBarDrawer>
    </NavbarCollapseProvider>
  );
};

const NavBarContainerWithContext = (props: any) => {
  const { isCollapsed } = useNavbarCollapse();
  return <NavBarContainer {...props} isCollapsed={isCollapsed} />;
};

export const NavBarContainer = styled('div', {
  shouldForwardProp: propName => propName !== 'isCollapsed'
})<{ isCollapsed: boolean }>`
  --navbar-width: ${({ isCollapsed }) => (isCollapsed ? NAVBAR_COLLAPSED_WIDTH : NAVBAR_WIDTH)};
  width: var(--navbar-width);
  ${legacyOnly(
    theme => css`
      border-right: 1px solid ${theme.separatorGray};
    `
  )}
  /* Figma's Navigation/Main has no right border -- the dark surface is the boundary. */
  ${newDesignOnly(css`
    padding: var(--spacing-xs) 0;
  `)}
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  ${legacyOnly(
    theme => css`
      background-color: ${theme.backgroundGray};
    `
  )}
  /* Dark in both colour modes: the rail is a dark surface by design, not a themed one. */
  ${newDesignOnly(css`
    background-color: var(--surface-navigation-navigation);
  `)}
  overflow-x: visible;
  overflow-y: hidden;
  transition: width ${NAVBAR_TRANSITION_DURATION} cubic-bezier(0.25, 0.46, 0.45, 0.94);
`;

const MainContainer = styled.div`
  flex: 1;
  height: 100%;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
`;

const PanelsContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  height: 100%;
`;

export default NavBar;
